import { useState, useCallback } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../../Keycloak/useAuthenticatedFetchWithWorkspace";
import { Customer } from "../../../types/Customer";

const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

const readCache = (key: string): Record<string, unknown> | null => {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const { data, expiresAt } = JSON.parse(raw) as { data: Record<string, unknown>; expiresAt: number };
        if (Date.now() > expiresAt) { sessionStorage.removeItem(key); return null; }
        return data;
    } catch { return null; }
};

const writeCache = (key: string, data: Record<string, unknown>): void => {
    try { sessionStorage.setItem(key, JSON.stringify({ data, expiresAt: Date.now() + CACHE_TTL_MS })); } catch { /* quota dépassé */ }
};

const mapSynthesisToCustomer = (data: Record<string, unknown>): Customer => {
    const per = (data.PER_PERSON as Record<string, unknown>) || {};
    const comms = (data.MAIN_COMMS as Record<string, unknown>) || {};
    const mainIdentifiers = (data.MAIN_IDENTIFIERS as Record<string, unknown>) || {};

    const postalAddr = (comms.MAIN_POSTAL_ADDRESS || data.COMM_POSTAL_ADDRESS) as Record<string, unknown> | null;
    const cellPhone = comms.MAIN_CELL_PHONE as Record<string, unknown> | null;
    const phone = comms.MAIN_PHONE as Record<string, unknown> | null;
    const email = comms.MAIN_EMAIL as Record<string, unknown> | null;
    const mainCard = (mainIdentifiers.MAIN_CARD as Record<string, unknown>) || null;
    const entEntity = (data.ENT_ENTITY as Record<string, unknown>) || null;

    return {
        ...data,
        PERSONID: (per.PERSONID ?? per.PERSON_ID ?? data.PERSONID ?? data.PERSON_ID ?? "") as string | number,
        LASTNAME: (per.LASTNAME ?? per.NAME ?? data.LASTNAME ?? data.NAME ?? "") as string,
        FIRSTNAME: (per.FIRSTNAME ?? data.FIRSTNAME ?? "") as string,
        CIVILITY: (per.CIVILITY ?? data.CIVILITY) as string,
        BIRTH_DATE: (per.BIRTH_DATE ?? per.BIRTHDATE ?? data.BIRTH_DATE ?? data.BIRTHDATE ?? "") as string,
        SUBSCRIBE_DATE: (per.SUBSCRIBE_DATE ?? data.SUBSCRIBE_DATE) as string,
        IDENTIFIER: mainCard ? (mainCard.IDENTIFIER_VALUE ?? mainCard.EAN) as string : data.IDENTIFIER as string,
        WEB_IDENTIFIER: (per.WEB_IDENTIFIER ?? data.WEB_IDENTIFIER) as string,
        ADDRESS1: postalAddr ? (postalAddr.ADDRESS1 ?? postalAddr.ADDRESS) as string : data.ADDRESS1 as string,
        ADDRESS2: postalAddr ? postalAddr.ADDRESS2 as string : data.ADDRESS2 as string,
        ADDRESS3: postalAddr ? postalAddr.ADDRESS3 as string : data.ADDRESS3 as string,
        ADDRESS4: postalAddr ? postalAddr.ADDRESS4 as string : data.ADDRESS4 as string,
        ZIP_CODE: postalAddr ? (postalAddr.ZIP_CODE ?? postalAddr.ZIPCODE) as string : (data.ZIP_CODE ?? data.ZIPCODE) as string,
        CITY: postalAddr ? (postalAddr.LOCAL_CITY_NAME ?? postalAddr.CITY) as string : data.CITY as string,
        COUNTRY: postalAddr ? (postalAddr.LOCAL_COUNTRY_NAME ?? postalAddr.COUNTRY) as string : data.COUNTRY as string,
        EMAIL: email ? (email.EMAIL_ADDRESS ?? email.EMAIL_VALUE ?? email.EMAIL) as string : data.EMAIL as string,
        CELLPHONE: cellPhone ? (cellPhone.CELL_PHONE_NUMBER ?? cellPhone.CELLPHONE_VALUE ?? cellPhone.PHONE_VALUE) as string : data.CELLPHONE as string,
        PHONE: phone ? (phone.PHONE_NUMBER ?? phone.PHONE_VALUE ?? phone.PHONE) as string : data.PHONE as string,
        STORE_ID: (per.STORE_ID ?? data.STORE_ID ?? entEntity?.ENTITY_ID) as string | number,
        STORE_NAME: entEntity
            ? (([entEntity.IDENTIFIER, entEntity.LOCAL_CITY_NAME ?? entEntity.CITY ?? entEntity.SHORT_CITY_NAME].filter(Boolean).join(' – ') || entEntity.NAME) as string)
            : (per.STORE_NAME ?? data.STORE_NAME ?? '') as string,
        FORM_NUMBER: (per.FORM_NUMBER ?? data.FORM_NUMBER) as string,
        FORM_LOT_NUMBER: (per.FORM_LOT_NUMBER ?? data.FORM_LOT_NUMBER) as string,
        UPDATE_DATE: (per.UPDATE_DATE ?? data.UPDATE_DATE) as string | undefined,
    };
};

const useGetPersonById = () => {
    const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const getPersonById = useCallback(
        async (personId: string | number): Promise<void> => {
            if (!isReady) return;

            const cacheKey = `synth_${workspaceId ?? "default"}_${personId}`;
            const cached = readCache(cacheKey);
            if (cached) {
                setCustomer(mapSynthesisToCustomer(cached));
                return;
            }

            setIsLoading(true);
            setError("");
            try {
                const result = await authFetch(
                    `/getSynthesisPerson?person_id=${encodeURIComponent(String(personId))}`,
                    { method: "GET" }
                );
                if (result && typeof result === "object" && !Array.isArray(result)) {
                    const data = result as Record<string, unknown>;
                    writeCache(cacheKey, data);
                    setCustomer(mapSynthesisToCustomer(data));
                } else if (Array.isArray(result) && result.length > 0) {
                    const data = result[0] as Record<string, unknown>;
                    writeCache(cacheKey, data);
                    setCustomer(mapSynthesisToCustomer(data));
                } else {
                    setError("Client introuvable");
                }
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : "An unknown error occurred");
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, isReady, workspaceId]
    );

    const resetPerson = useCallback(() => {
        setCustomer(null);
        setError("");
    }, []);

    const invalidateCache = useCallback((personId: string | number) => {
        const cacheKey = `synth_${workspaceId ?? "default"}_${personId}`;
        try { sessionStorage.removeItem(cacheKey); } catch { /* ignore */ }
    }, [workspaceId]);

    return { customer, isLoading, error, getPersonById, resetPerson, invalidateCache, isReady };
};

export default useGetPersonById;

